import { Box, Typography } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../../../core-components/theme/color-palette/colors';
import { SurfaceCard } from '../../../../core-components/theme/components/SurfaceCard';
import { AlwaysReachesLine } from '../../../close-the-loop';
import type { AlwaysReachesKey, HearLine } from '../../../close-the-loop';
import { hearCardSx, hearListSx } from '../../constants/layout';
import type { PlantScope } from '../../domain/hearLines';
import { stepGateOf } from '../../domain/steps';
import { useOnboardingHear } from '../../hooks/useOnboardingHear';
import type { HearContent } from '../../hooks/useOnboardingHear';
import { gatedDockOf, SettingsGate } from '../SettingsGate';
import { StepFrame } from '../StepFrame';
import type { WizardNav } from '../StepFrame';

import { hearLineText, roleScopeText } from './hearText';

const cardTitleSx = { color: Neutrals[900] } as const;

const RoleLines: React.FC<{ heading: string; lines: readonly HearLine[] }> = ({ heading, lines }) => {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <SurfaceCard sx={hearCardSx}>
      <Typography id={headingId} variant="cardTitleSm" component="h2" sx={cardTitleSx}>
        {heading}
      </Typography>
      <Box component="ul" aria-labelledby={headingId} sx={hearListSx}>
        {lines.map((line) => (
          <Typography key={line} component="li" variant="body">
            {hearLineText(t, line)}
          </Typography>
        ))}
      </Box>
    </SurfaceCard>
  );
};

const AlwaysReachesCard: React.FC<{ items: readonly AlwaysReachesKey[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
    <SurfaceCard sx={hearCardSx}>
      <Typography variant="cardTitleSm" component="h2" sx={cardTitleSx}>
        {t('closeTheLoop.settings.alwaysReachesYou', 'Always reaches you')}
      </Typography>
      <AlwaysReachesLine items={items} variant="list" />
    </SurfaceCard>
  );
};

const HearBody: React.FC<{ content: HearContent; scope: PlantScope }> = ({ content, scope }) => {
  const { t } = useTranslation();
  switch (content.kind) {
    case 'role':
      return <RoleLines heading={roleScopeText(t, content.roleGroup, scope)} lines={content.lines} />;
    case 'unknownRole':
      return <AlwaysReachesCard items={content.alwaysReaches} />;
  }
};

export const HearStep: React.FC<{ nav: WizardNav }> = ({ nav }) => {
  const { t } = useTranslation();
  const hear = useOnboardingHear();
  const { content } = hear;
  return (
    <StepFrame
      nav={nav}
      title={t('onboarding.title.hear', 'What you’ll hear about')}
      {...gatedDockOf(t, stepGateOf(hear.load), nav.next, {
        primary: { label: t('onboarding.continue', 'Continue'), onPress: nav.next },
      })}
    >
      <SettingsGate load={hear.load}>{content !== null && <HearBody content={content} scope={hear.scope} />}</SettingsGate>
    </StepFrame>
  );
};
